export type RecordingState = "idle" | "recording" | "stopped" | "error";

export interface InterviewRecordingResult {
  blob: Blob;
  mimeType: string;
  size: number;
  url: string;
}

interface InterviewRecorderOptions {
  audioContext?: AudioContext;
  onStateChange?: (state: RecordingState) => void;
  // 出力形式。"webm" は MediaRecorder の収録 blob(audio/webm;codecs=opus 等)をそのまま返す（圧縮済みで軽量・推奨）。
  // "wav" は decodeAudioData で PCM16 WAV に変換して返す（互換性重視だが大容量）。
  outputFormat?: "webm" | "wav";
}

const STOP_TIMEOUT_MS = 5000;

function resolveRecordingMimeType(): string {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/ogg"];

  for (const candidate of candidates) {
    if (MediaRecorder.isTypeSupported(candidate)) {
      return candidate;
    }
  }

  return "";
}

function writeWaveString(view: DataView, offset: number, value: string) {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

function toWaveBlob(audioBuffer: AudioBuffer): Blob {
  const channelCount = audioBuffer.numberOfChannels;
  const bytesPerSample = 2;
  const blockAlign = channelCount * bytesPerSample;
  const dataLength = audioBuffer.length * blockAlign;
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);

  writeWaveString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataLength, true);
  writeWaveString(view, 8, "WAVE");
  writeWaveString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channelCount, true);
  view.setUint32(24, audioBuffer.sampleRate, true);
  view.setUint32(28, audioBuffer.sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeWaveString(view, 36, "data");
  view.setUint32(40, dataLength, true);

  const channels = Array.from({ length: channelCount }, (_, channelIndex) =>
    audioBuffer.getChannelData(channelIndex)
  );

  let offset = 44;
  for (let frameIndex = 0; frameIndex < audioBuffer.length; frameIndex += 1) {
    for (let channelIndex = 0; channelIndex < channelCount; channelIndex += 1) {
      const sample = Math.max(-1, Math.min(1, channels[channelIndex]?.[frameIndex] ?? 0));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += bytesPerSample;
    }
  }

  return new Blob([buffer], { type: "audio/wav" });
}

async function convertBlobToWave(audioContext: AudioContext, blob: Blob): Promise<Blob | null> {
  const input = await blob.arrayBuffer();
  const audioBuffer = await audioContext.decodeAudioData(input.slice(0));
  if (audioBuffer.length === 0) {
    return null;
  }

  return toWaveBlob(audioBuffer);
}

export class InterviewRecorder {
  private readonly aiInputNode: GainNode;
  private readonly audioContext: AudioContext;
  private readonly candidateInputNode: GainNode;
  private readonly destinationNode: MediaStreamAudioDestinationNode;
  private readonly onStateChange: ((state: RecordingState) => void) | undefined;
  private readonly outputFormat: "webm" | "wav";
  private readonly ownContext: boolean;
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private lastResult: InterviewRecordingResult | null = null;
  private state: RecordingState = "idle";

  constructor(options: InterviewRecorderOptions = {}) {
    this.audioContext = options.audioContext ?? new AudioContext();
    this.ownContext = !options.audioContext;
    this.onStateChange = options.onStateChange;
    this.outputFormat = options.outputFormat ?? "webm";
    this.destinationNode = this.audioContext.createMediaStreamDestination();
    this.aiInputNode = this.audioContext.createGain();
    this.candidateInputNode = this.audioContext.createGain();
    this.aiInputNode.gain.value = 1;
    this.candidateInputNode.gain.value = 1;
    this.aiInputNode.connect(this.destinationNode);
    this.candidateInputNode.connect(this.destinationNode);
  }

  get context() {
    return this.audioContext;
  }

  get aiDestination() {
    return this.aiInputNode;
  }

  get candidateDestination() {
    return this.candidateInputNode;
  }

  async start() {
    if (this.state === "recording") {
      return;
    }

    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }

    this.revokeLastResult();
    this.chunks = [];

    const mimeType = resolveRecordingMimeType();
    this.mediaRecorder =
      mimeType.length > 0
        ? new MediaRecorder(this.destinationNode.stream, { mimeType })
        : new MediaRecorder(this.destinationNode.stream);

    this.mediaRecorder.addEventListener("dataavailable", (event) => {
      if (event.data.size > 0) {
        this.chunks.push(event.data);
      }
    });

    this.mediaRecorder.addEventListener("error", () => {
      this.setState("error");
    });

    this.mediaRecorder.start();
    this.setState("recording");
  }

  async stop(): Promise<InterviewRecordingResult | null> {
    const recorder = this.mediaRecorder;
    if (!recorder) {
      return this.lastResult;
    }

    if (recorder.state === "inactive") {
      this.setState(this.lastResult ? "stopped" : "idle");
      return this.lastResult;
    }

    return await new Promise<InterviewRecordingResult | null>((resolve) => {
      let finalized = false;

      const finalize = async () => {
        if (finalized) {
          return;
        }

        finalized = true;
        window.clearTimeout(stopTimeout);
        const fallbackMimeType =
          recorder.mimeType.length > 0 ? recorder.mimeType : this.chunks[0]?.type || "audio/webm";
        const fallbackBlob = new Blob(this.chunks, { type: fallbackMimeType });
        this.chunks = [];
        this.mediaRecorder = null;

        if (fallbackBlob.size === 0) {
          this.lastResult = null;
          this.setState("error");
          resolve(null);
          return;
        }

        let blob = fallbackBlob;
        let mimeType = fallbackMimeType;

        if (this.outputFormat === "wav") {
          try {
            const waveBlob = await convertBlobToWave(this.audioContext, fallbackBlob);
            if (waveBlob && waveBlob.size > 0) {
              blob = waveBlob;
              mimeType = waveBlob.type;
            }
          } catch {
            blob = fallbackBlob;
            mimeType = fallbackMimeType;
          }
        }

        const url = URL.createObjectURL(blob);
        this.lastResult = {
          blob,
          mimeType,
          size: blob.size,
          url,
        };
        this.setState("stopped");
        resolve(this.lastResult);
      };

      const stopTimeout = window.setTimeout(() => {
        void finalize();
      }, STOP_TIMEOUT_MS);

      recorder.addEventListener(
        "stop",
        () => {
          void finalize();
        },
        { once: true }
      );

      try {
        recorder.requestData();
      } catch {}

      try {
        recorder.stop();
      } catch {
        void finalize();
      }
    });
  }

  async close() {
    await this.stop();
    this.revokeLastResult();
    this.aiInputNode.disconnect();
    this.candidateInputNode.disconnect();
    this.destinationNode.disconnect();

    if (this.ownContext && this.audioContext.state !== "closed") {
      await this.audioContext.close();
    }
  }

  private revokeLastResult() {
    if (!this.lastResult) {
      return;
    }

    URL.revokeObjectURL(this.lastResult.url);
    this.lastResult = null;
  }

  private setState(nextState: RecordingState) {
    if (this.state === nextState) {
      return;
    }

    this.state = nextState;
    this.onStateChange?.(nextState);
  }
}
